import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Plus, 
  Download, 
  Calendar, 
  DollarSign, 
  User, 
  Clock, 
  TrendingUp,
  CheckCircle,
  AlertCircle,
  CreditCard,
  Shield,
  Check,
  X,
  Send,
  FileText,
  Users,
  MessageSquare,
  RefreshCw,
  ChevronDown
} from 'lucide-react';
import { PermissionGate } from '../components/PermissionGate';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../utils/permissions';
import { useMemberLoans, useLoanRepaymentsByLoan, useCreateRepayment, useUpdateRepayment, useDeleteRepayment, usePayLoanViaMpesa, useExportLoanRepayments } from '../hooks/useLoanRepayments.ts';
import { useIsAdmin } from '../hooks/useIsAdmin.ts';
import { useGetProfile } from '../hooks/useProfile.ts';
import { useLoanGuarantors, useSendGuarantorRequests } from '../hooks/useLoanGuarantors.ts';
import Modal from '../components/Modal.tsx';
import ExportFormatModal from '../components/ExportFormatModal.tsx';
import LoanRepaymentTimeline from '../components/LoanRepaymentTimeline.tsx';
import LoanDocumentViewer from '../components/LoanDocumentViewer.js';
import LoanStatusTimeline from '../components/LoanStatusTimeline.js';
import GuarantorRequestForm from '../components/GuarantorRequestForm.js';
import ReapplyLoanForm from '../components/ReapplyLoanForm.js';
import LoanScheduleTable from '../components/LoanScheduleTable.tsx';
import LoanWriteOffForm from '../components/LoanWriteOffForm.js';
import LoanRestructureForm from '../components/LoanRestructureForm.js';
import LoanCollateralForm from '../components/LoanCollateralForm.js';
import LoanRefinanceForm from '../components/LoanRefinanceForm.js';
import { formatCurrency, PAYMENT_METHOD_OPTIONS } from '../hooks/useLoanRepayments.ts';
import { toast } from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import getApiErrorMessage from '../utils/helper.js';
import api from '../utils/axios.ts';

// Status badge component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    PENDING: { label: 'Pending Approval', color: 'yellow' },
    APPROVED: { label: 'Approved', color: 'green' },
    DISAPPROVED: { label: 'Disapproved', color: 'red' },
    ACTIVE: { label: 'Active', color: 'blue' },
    COMPLETED: { label: 'Completed', color: 'gray' },
  };
  
  const config = statusConfig[status] || { label: status, color: 'gray' };
  
  const colorClasses = {
    yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    green: 'bg-green-100 text-green-800 border-green-200',
    red: 'bg-red-100 text-red-800 border-red-200',
    blue: 'bg-blue-100 text-blue-800 border-blue-200',
    gray: 'bg-gray-100 text-gray-800 border-gray-200',
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${colorClasses[config.color]}`}>
      {config.label}
    </span>
  );
};

export default function LoanDetailsPage() {
  const { loanId } = useParams();
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const { data: userProfile } = useGetProfile();
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  
  const [isRepaymentModalOpen, setIsRepaymentModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isMpesaModalOpen, setIsMpesaModalOpen] = useState(false);
  const [selectedRepayment, setSelectedRepayment] = useState(null);
  
  // Approval workflow states
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [approvalAction, setApprovalAction] = useState(null); // 'approve' or 'disapprove'
  const [approvalNotes, setApprovalNotes] = useState('');
  const [isApprovalLoading, setIsApprovalLoading] = useState(false);
  const [disbursementModalOpen, setDisbursementModalOpen] = useState(false);
  const [disbursementNotes, setDisbursementNotes] = useState('');
  const [disbursementDate, setDisbursementDate] = useState('');
  const [sendSmsNotification, setSendSmsNotification] = useState(true);
  const [isDisbursementLoading, setIsDisbursementLoading] = useState(false);
  const [showApprovalTimeline, setShowApprovalTimeline] = useState(false);
  const [showRepaymentExportModal, setShowRepaymentExportModal] = useState(false);
  const [reapplyModalOpen, setReapplyModalOpen] = useState(false);
  
  // New loan action modals
  const [writeOffModalOpen, setWriteOffModalOpen] = useState(false);
  const [restructureModalOpen, setRestructureModalOpen] = useState(false);
  const [collateralModalOpen, setCollateralModalOpen] = useState(false);
  const [refinanceModalOpen, setRefinanceModalOpen] = useState(false);
  const [loanActionsDropdownOpen, setLoanActionsDropdownOpen] = useState(false);
  
  // Fetch loan and repayment data
  const { data: loanRepaymentsData, isLoading: repaymentsLoading } = useLoanRepaymentsByLoan(parseInt(loanId));
  const createRepayment = useCreateRepayment();
  const updateRepayment = useUpdateRepayment();
  const deleteRepayment = useDeleteRepayment();
  const payLoanViaMpesa = usePayLoanViaMpesa();
  const exportLoanRepayments = useExportLoanRepayments();

  // Fetch guarantor data
  const { data: guarantorsData, isLoading: guarantorsLoading } = useLoanGuarantors(parseInt(loanId));
  const sendGuarantorRequests = useSendGuarantorRequests();

  // Guarantor request states
  const [guarantorModalOpen, setGuarantorModalOpen] = useState(false);
  const [selectedGuarantors, setSelectedGuarantors] = useState([]);

  const loanData = loanRepaymentsData?.data?.loan;
  const repayments = loanRepaymentsData?.data?.repayments || [];
  const summary = loanRepaymentsData?.data?.summary;

  // Approval workflow functions
  const handleApprovalAction = (action) => {
    setApprovalAction(action);
    setApprovalModalOpen(true);
    setApprovalNotes('');
  };

  const submitApproval = async () => {
    if (!approvalAction) return;

    setIsApprovalLoading(true);
    try {
      const endpoint = approvalAction === 'approve' ? `/memberloan/approve/${loanId}` : `/memberloan/disapprove/${loanId}`;
      const payload = {
        id: parseInt(loanId),
        reason: approvalNotes,
        send_sms: true
      };

      await api.post(endpoint, payload);
      
      toast.success(`Loan ${approvalAction === 'approve' ? 'approved' : 'disapproved'} successfully`);
      
      // Refresh data
      queryClient.invalidateQueries(['loanRepaymentsByLoan']);
      queryClient.invalidateQueries(['memberLoans']);
      
      setApprovalModalOpen(false);
      setApprovalAction(null);
      setApprovalNotes('');
    } catch (error) {
      const errorMessage = getApiErrorMessage(error);
      toast.error(errorMessage || `Failed to ${approvalAction} loan`);
    } finally {
      setIsApprovalLoading(false);
    }
  };

  const handleDisbursement = () => {
    setDisbursementModalOpen(true);
    setDisbursementNotes('');
    setDisbursementDate(new Date().toISOString().slice(0, 19).replace('T', ' '));
    setSendSmsNotification(true);
  };

  const submitDisbursement = async () => {
    setIsDisbursementLoading(true);
    try {
      const payload = {
        disbursement_notes: disbursementNotes,
        disbursed_at: disbursementDate,
        send_sms: sendSmsNotification,
      };

      await api.post(`/memberloan/${loanId}/disburse`, payload);
      
      toast.success('Loan disbursed successfully');
      
      // Refresh data
      queryClient.invalidateQueries(['loanRepaymentsByLoan']);
      queryClient.invalidateQueries(['memberLoans']);
      
      setDisbursementModalOpen(false);
      setDisbursementNotes('');
      setDisbursementDate('');
      setSendSmsNotification(true);
    } catch (error) {
      const errorMessage = getApiErrorMessage(error);
      toast.error(errorMessage || 'Failed to disburse loan');
    } finally {
      setIsDisbursementLoading(false);
    }
  };

  const handleCreateRepayment = (repaymentData) => {
    createRepayment.mutate(repaymentData, {
      onSuccess: () => {
        setIsRepaymentModalOpen(false);
      }
    });
  };

  const handleEditRepayment = (repayment) => {
    setSelectedRepayment(repayment);
    setIsEditModalOpen(true);
  };

  const handleUpdateRepayment = (repaymentData) => {
    updateRepayment.mutate({ id: selectedRepayment.id, ...repaymentData }, {
      onSuccess: () => {
        setIsEditModalOpen(false);
        setSelectedRepayment(null);
      }
    });
  };

  const handleDeleteRepayment = (repaymentId) => {
    if (window.confirm('Are you sure you want to delete this repayment? This action cannot be undone.')) {
      deleteRepayment.mutate(repaymentId);
    }
  };

  const handleMpesaPayment = (paymentData) => {
    payLoanViaMpesa.mutate(paymentData, {
      onSuccess: () => {
        setIsMpesaModalOpen(false);
      }
    });
  };

  const handleSendGuarantorRequests = () => {
    const guarantors = selectedGuarantors.map(memberId => ({
      member_id: memberId
    }));

    sendGuarantorRequests.mutate({
      loanId: parseInt(loanId),
      guarantors
    }, {
      onSuccess: () => {
        setGuarantorModalOpen(false);
        setSelectedGuarantors([]);
      }
    });
  };

  const handleGuarantorSelection = (userId, checked) => {
    if (checked) {
      setSelectedGuarantors(prev => [...prev, userId]);
    } else {
      setSelectedGuarantors(prev => prev.filter(id => id !== userId));
    }
  };

  const handleViewDocument = async (loanId, documentId, fileName) => {
    try {
      const response = await api.get(`/memberloan/${loanId}/documents/${documentId}/download`, {
        responseType: 'blob',
      });
      
      // Create blob URL and open in new tab for viewing
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      
      // Clean up the blob URL after a delay
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
    } catch (error) {
      console.error('View failed:', error);
      alert('Failed to view document. Please try again.');
    }
  };

  const handleDownloadDocument = async (loanId, documentId, fileName) => {
    try {
      const response = await api.get(`/memberloan/${loanId}/documents/${documentId}/download`, {
        responseType: 'blob',
      });
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download document. Please try again.');
    }
  };

  if (repaymentsLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading loan details...</span>
        </div>
      </div>
    );
  }

  if (!loanData) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <AlertCircle size={48} className="mx-auto text-red-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-700 mb-2">Loan Not Found</h3>
          <p className="text-gray-500 mb-4">The requested loan could not be found.</p>
          <button
            onClick={() => navigate('/member-loans')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Back to Loans
          </button>
        </div>
      </div>
    );
  }

  const progressPercentage = loanData.total_amount > 0 && summary?.total_repaid
    ? (summary.total_repaid / loanData.total_amount) * 100 
    : 0;

  const isFullyPaid = loanData.loan_balance <= 0;
  const nextPaymentDue = calculateNextPaymentDue(loanData, repayments);
  
  // Get installment period label from loan data
  const installmentLabel = loanData.installment_period_label || 'Monthly Installment';

  // Check if guarantor requirements are met
  const guarantorRequirementsMet = () => {
    if (!loanData.loan_type?.requires_guarantors) {
      return true;
    }
    const requiredAmount = loanData.guarantor_amount || 0;
    const confirmedAmount = loanData.guarantor_amount_confirmed || 0;
    return confirmedAmount >= requiredAmount;
  };

  // Check if loan can be approved/disbursed
  const canBeApproved = loanData.status === 'PENDING' && guarantorRequirementsMet();
  const canBeDisbursed = loanData.status === 'APPROVED';
  
  // Check if loan actions are available (for active loans)
  const canPerformLoanActions = loanData.status === 'ACTIVE' && loanData.loan_balance > 0;

  return (
    
      <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/member-loans')}
            className="mr-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Loan Details</h1>
            <p className="text-gray-600 mt-1">
              {loanData.member?.name} • {loanData.loan_type?.name}
            </p>
          </div>
        </div>
        
        <div className="flex gap-2">
          {/* Approval workflow actions for admins */}
          {can(PERMISSIONS.APPROVE_LOAN) && loanData.status === 'PENDING' && (
            <>
              <div className="relative group">
                <button 
                  onClick={() => handleApprovalAction('approve')}
                  disabled={!guarantorRequirementsMet()}
                  className={`flex items-center px-4 py-2 rounded-lg ${
                    guarantorRequirementsMet() 
                      ? 'bg-green-600 text-white hover:bg-green-700' 
                      : 'bg-gray-400 text-white cursor-not-allowed'
                  }`}
                  title={guarantorRequirementsMet() ? "Approve Loan" : "Guarantor requirements not met"}
                >
                  <Check size={16} className="mr-2" />
                  Approve
                </button>

                {/* Tooltip for when guarantor requirements aren't met */}
                {!guarantorRequirementsMet() && (
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg z-50 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                    <div className="flex items-center">
                      <AlertCircle size={14} className="mr-1" />
                      Guarantor requirements not met
                    </div>
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                  </div>
                )}
              </div>
              <PermissionGate permission={PERMISSIONS.REJECT_LOAN}>
                <button 
                  onClick={() => handleApprovalAction('disapprove')}
                  className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  title="Disapprove Loan"
                >
                  <X size={16} className="mr-2" />
                  Disapprove
                </button>
              </PermissionGate>
            </>
          )}
          
          {can(PERMISSIONS.APPROVE_LOAN) && canBeDisbursed && (
            <button 
              onClick={handleDisbursement}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              title="Disburse Loan"
            >
              <Send size={16} className="mr-2" />
              Disburse
            </button>
          )}
          
          {/* Send Guarantor Requests - Only for loan applicant when loan is pending and requires guarantors */}
          {!isAdmin && 
           loanData.status === 'PENDING' && 
           loanData.loan_type?.requires_guarantors && 
           (!guarantorsData || guarantorsData.length === 0 || 
            (guarantorsData && 
             (guarantorsData.filter(g => g.status === 'accepted').length === 0 || 
              (loanData.guarantor_amount && 
               guarantorsData.filter(g => g.status === 'accepted').reduce((sum, g) => sum + (g.guaranteed_amount || 0), 0) < loanData.guarantor_amount)))) && (
            <button 
              onClick={() => setGuarantorModalOpen(true)}
              className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              title="Send Guarantor Requests"
            >
              <Users size={16} className="mr-2" />
              Send Guarantor Requests
            </button>
          )}
          
          {/* Reapply Loan Button - Only for admins when loan can be reapplied */}
          {can(PERMISSIONS.APPROVE_LOAN) && loanData.can_be_reapplied && (
            <button 
              onClick={() => setReapplyModalOpen(true)}
              className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              title="Reapply Loan"
            >
              <RefreshCw size={16} className="mr-2" />
              Reapply Loan
            </button>
          )}
          
          {/* Loan Actions Dropdown - For active loans */}
          {canPerformLoanActions && can(PERMISSIONS.EDIT_LOAN) && (
            <div className="relative">
              <button 
                onClick={() => setLoanActionsDropdownOpen(!loanActionsDropdownOpen)}
                className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                title="Loan Actions"
              >
                <FileText size={16} className="mr-2" />
                Loan Actions
                <ChevronDown size={16} className={`ml-2 transition-transform ${loanActionsDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {loanActionsDropdownOpen && (
                <>
                  {/* Backdrop to close dropdown when clicking outside */}
                  <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setLoanActionsDropdownOpen(false)}
                  />
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                    <div className="py-1">
                      <button
                        onClick={() => {
                          setCollateralModalOpen(true);
                          setLoanActionsDropdownOpen(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                      >
                        <Shield size={16} className="mr-2" />
                        Add Collateral
                      </button>
                      <button
                        onClick={() => {
                          setRestructureModalOpen(true);
                          setLoanActionsDropdownOpen(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                      >
                        <RefreshCw size={16} className="mr-2" />
                        Restructure Loan
                      </button>
                      <button
                        onClick={() => {
                          setRefinanceModalOpen(true);
                          setLoanActionsDropdownOpen(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                      >
                        <TrendingUp size={16} className="mr-2" />
                        Refinance Loan
                      </button>
                      {can(PERMISSIONS.APPROVE_LOAN) && (
                        <button
                          onClick={() => {
                            setWriteOffModalOpen(true);
                            setLoanActionsDropdownOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center border-t border-gray-200"
                        >
                          <X size={16} className="mr-2" />
                          Write-Off Loan
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          
          {/* Download Statement Button */}
          <button 
            onClick={async () => {
              try {
                const response = await api.get(`/memberloan/${loanId}/statement`, {
                  responseType: 'blob'
                });
                
                // Create a blob URL and trigger download
                const blob = new Blob([response.data], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `loan_statement_${loanData?.loan_no || loanId}_${new Date().toISOString().split('T')[0]}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
              } catch (error) {
                console.error('Error downloading statement:', error);
                toast.error('Failed to download statement');
              }
            }}
            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            title="Download Statement"
          >
            <Download size={16} className="mr-2" />
            Statement
          </button>

          {!isFullyPaid && (
            <>
              <div className="relative group">
                <button 
                  onClick={() => {
                    if (userProfile?.data?.mpesa_credentials) {
                      setIsMpesaModalOpen(true);
                    }
                  }}
                  className={`flex items-center px-4 py-2 rounded-lg ${
                    userProfile?.data?.mpesa_credentials 
                      ? 'bg-green-600 text-white hover:bg-green-700' 
                      : 'bg-gray-400 text-white cursor-not-allowed'
                  }`}
                  disabled={!userProfile?.data?.mpesa_credentials}
                >
                  <CreditCard size={16} className="mr-2" />
                  Pay via M-Pesa
                </button>

                {/* Tooltip */}
                {!userProfile?.data?.mpesa_credentials && (
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg z-50 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                    <div className="flex items-center">
                      <AlertCircle size={14} className="mr-1" />
                      M-Pesa integration not configured
                    </div>
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                  </div>
                )}
              </div>
              <PermissionGate permission={PERMISSIONS.ADD_LOAN_REPAYMENT}>
                <button 
                  onClick={() => setIsRepaymentModalOpen(true)}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Plus size={16} className="mr-2" />
                  Add Payment
                </button>
              </PermissionGate>
            </>
          )}
        </div>
      </div>

      {/* Loan Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 kpi-grid">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Principal Amount</p>
              <h3 className="text-2xl font-bold">{formatCurrency(loanData.principal)}</h3>
            </div>
            <DollarSign size={32} className="text-blue-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Total Repaid</p>
              <h3 className="text-2xl font-bold">{formatCurrency(summary?.total_repaid ?? 0)}</h3>
            </div>
            <CheckCircle size={32} className="text-green-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm">Outstanding Balance</p>
              <h3 className="text-2xl font-bold">{formatCurrency(loanData.loan_balance)}</h3>
            </div>
            <TrendingUp size={32} className="text-orange-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Progress</p>
              <h3 className="text-2xl font-bold">{Math.round(progressPercentage)}%</h3>
            </div>
            <Clock size={32} className="text-purple-200" />
          </div>
        </div>
      </div>

      {/* Reapply Loan Information */}
      {loanData.is_reapply_loan && (
        <div className="bg-white rounded-xl shadow p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <RefreshCw size={20} className="mr-2 text-purple-600" />
            Reapply Loan Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Original Loan</label>
                <p className="mt-1 font-medium">
                  {loanData.reapply_from_loan?.loan_no} - {formatCurrency(loanData.reapply_from_loan?.principal)}
                </p>
                <p className="text-sm text-gray-500">
                  Balance at reapply: {formatCurrency(loanData.original_loan_balance)}
                </p>
              </div>
              

            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Top-up Amount</label>
                <p className="mt-1 font-medium text-green-600">{formatCurrency(loanData.top_up_amount)}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-500">Reapply Reason</label>
                <p className="mt-1 font-medium">{loanData.reapply_reason || 'Not specified'}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loan Information Panel */}
      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Loan Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Borrower</label>
              <div className="flex items-center mt-1">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                  <User size={16} className="text-blue-600" />
                </div>
                <div>
                  <p className="font-medium">{loanData.member?.name}</p>
                  <p className="text-sm text-gray-500">{loanData.member?.phone_number}</p>
                </div>
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-500">Loan Type</label>
              <p className="mt-1 font-medium">{loanData.loan_type?.name}</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Start Date</label>
              <p className="mt-1 font-medium">{new Date(loanData.start_date).toLocaleDateString()}</p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-500">End Date</label>
              <p className="mt-1 font-medium">{new Date(loanData.end_date).toLocaleDateString()}</p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-500">Duration</label>
              <p className="mt-1 font-medium">{loanData.formatted_duration || `${loanData.duration} `} {loanData.loan_type?.duration_unit}</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Interest Rate</label>
              <p className="mt-1 font-medium">{loanData.rate}% {loanData.rate_period_label || 'per month'}</p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-500">Interest Amount</label>
              <p className="mt-1 font-medium">{formatCurrency(loanData.interest || 0)}</p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-500">Processing Fees</label>
              <p className="mt-1 font-medium text-orange-600">{formatCurrency(loanData.total_fees || 0)}</p>
              {loanData.applied_fees && loanData.applied_fees.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">{loanData.applied_fees.length} fee(s) applied</p>
              )}
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-500">Disbursed Amount</label>
              <p className="mt-1 font-medium text-lg text-green-600">{formatCurrency(loanData.disbursed_amount || (loanData.principal - (loanData.total_fees || 0)))}</p>
              <p className="text-xs text-gray-500 mt-1">Amount member receives</p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-500">Total Amount</label>
              <p className="mt-1 font-medium text-lg text-blue-600">{formatCurrency(loanData.total_amount)}</p>
              <p className="text-xs text-gray-500 mt-1">Amount to repay</p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-500">Status</label>
              <div className="mt-1">
                <StatusBadge status={loanData.status} />
              </div>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-6">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Repayment Progress</span>
            <span>{Math.round(progressPercentage)}% Complete</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className={`h-3 rounded-full transition-all duration-1000 ease-out ${
                isFullyPaid ? 'bg-gradient-to-r from-green-500 to-green-600' : 'bg-gradient-to-r from-blue-500 to-purple-500'
              }`}
              style={{ width: `${Math.min(progressPercentage, 100)}%` }}
            />
          </div>
        </div>

        {/* Installment Information */}
        {!isFullyPaid && (
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h4 className="text-sm font-medium text-blue-900 mb-3">Installment Status</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-blue-700">Full Installments Paid</p>
                <p className="text-lg font-semibold text-blue-900">
                  {calculateFullInstallmentsPaid(loanData, repayments)} of {loanData.effective_duration || loanData.duration}
                </p>
              </div>
              <div>
                <p className="text-xs text-blue-700">Remaining Installments</p>
                <p className="text-lg font-semibold text-blue-900">
                  {calculateRemainingInstallments(loanData, repayments)}
                </p>
              </div>
              <div>
                <p className="text-blue-700">Current Period Status</p>
                <p className={`text-lg font-semibold ${
                  isCurrentMonthFullyPaid(loanData, repayments) 
                    ? 'text-green-600' 
                    : 'text-orange-600'
                }`}>
                  {/* {isCurrentMonthFullyPaid(loanData, repayments) ? 'Fully Paid' : 'Partially Paid'} */}
                </p>
                {!isCurrentMonthFullyPaid(loanData, repayments) && (
                  <p className="text-xs text-orange-600">
                    {formatCurrency(getCurrentInstallmentRemaining(loanData, repayments))} remaining
                  </p>
                )}
              </div>
            </div>
            {nextPaymentDue && (
              <div className="mt-3 pt-3 border-t border-blue-200">
                <p className="text-xs text-blue-700">Next Payment Due</p>
                <p className="text-sm font-medium text-blue-900">
                  {new Date(nextPaymentDue).toLocaleDateString()}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Current Installment Details */}
        {!isFullyPaid && !isCurrentMonthFullyPaid(loanData, repayments) && (
          <div className="mt-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-orange-900 mb-1">Current Installment Pending</h4>
                <p className="text-xs text-orange-700">Amount due for current period</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-orange-600">
                  {formatCurrency(getCurrentInstallmentRemaining(loanData, repayments))}
                </p>
                <p className="text-xs text-orange-600">out of {formatCurrency(loanData.monthly_installments)}</p>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-orange-200">
              <div className="flex justify-between text-xs text-orange-700">
                <span>{installmentLabel}</span>
                <span>{formatCurrency(loanData.monthly_installments)}</span>
              </div>
              <div className="flex justify-between text-xs text-orange-700">
                <span>Amount Paid</span>
                <span>{formatCurrency(getCurrentInstallmentPaid(loanData, repayments))}</span>
              </div>
              <div className="flex justify-between text-xs font-medium text-orange-900 mt-1">
                <span>Remaining</span>
                <span>{formatCurrency(getCurrentInstallmentRemaining(loanData, repayments))}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Approval Workflow Information */}
      {isAdmin && (
        <div className="bg-white rounded-xl shadow p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Shield size={20} className="mr-2 text-gray-600" />
            Approval Workflow
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Applied By</label>
                <div className="flex items-center mt-1">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                    <Users size={16} className="text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium">{loanData.applied_by?.name || 'N/A'}</p>
                    <p className="text-sm text-gray-500">
                      {loanData.created_at ? new Date(loanData.created_at).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Approved By</label>
                <div className="flex items-center mt-1">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <CheckCircle size={16} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium">{loanData.approved_by?.name || 'N/A'}</p>
                    <p className="text-sm text-gray-500">
                      {loanData?.approved_by?.created_at ? new Date(loanData.approved_by.created_at).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
              
              {loanData.approval_notes && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Approval Notes</label>
                  <p className="mt-1 text-sm bg-gray-50 p-2 rounded">{loanData.approval_notes}</p>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Disbursed By</label>
                <div className="flex items-center mt-1">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                    <Send size={16} className="text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium">{loanData.disbursed_by?.name || 'N/A'}</p>
                    <p className="text-sm text-gray-500">
                      {loanData.disbursed_at ? new Date(loanData.disbursed_at).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
              
              {loanData.disbursement_notes && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Disbursement Notes</label>
                  <p className="mt-1 text-sm bg-gray-50 p-2 rounded">{loanData.disbursement_notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Loan Processing Fees Breakdown */}
      {loanData.applied_fees && loanData.applied_fees.length > 0 && (
        <div className="bg-white rounded-xl shadow p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <DollarSign size={20} className="mr-2 text-orange-600" />
            Processing Fees Breakdown
          </h3>
          <div className="overflow-x-auto">
            <table className="responsive-table min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fee Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Calculation
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rate/Amount
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Charged Amount
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loanData.applied_fees.map((fee, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td data-label="Fee Name" className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{fee.fee_name}</div>
                    </td>
                    <td data-label="Type" className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                        {fee.fee_type}
                      </span>
                    </td>
                    <td data-label="Calculation" className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        fee.calculation_type === 'percentage' 
                          ? 'bg-purple-100 text-purple-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {fee.calculation_type === 'percentage' ? 'Percentage' : 'Fixed'}
                      </span>
                    </td>
                    <td data-label="Rate/Amount" className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                      {fee.calculation_type === 'percentage' 
                        ? `${fee.amount}%` 
                        : formatCurrency(fee.amount)}
                    </td>
                    <td data-label="Charged Amount" className="px-6 py-4 whitespace-nowrap text-right text-sm font-semibold text-gray-900">
                      {formatCurrency(fee.calculated_amount)}
                    </td>
                  </tr>
                ))}
                <tr className="bg-gray-50">
                  <td colSpan="4" className="px-6 py-4 text-right text-sm font-semibold text-gray-900">
                    Total Fees:
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-bold text-orange-600">
                    {formatCurrency(loanData.total_fees || 0)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          {/* Fee Calculation Summary */}
          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <h4 className="text-sm font-medium text-blue-900 mb-2">Loan Amount Breakdown</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-700">Principal Amount:</span>
                <span className="font-medium">{formatCurrency(loanData.principal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Interest ({loanData.rate}%):</span>
                <span className="font-medium">{formatCurrency(loanData.interest || 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Processing Fees (deducted):</span>
                <span className="font-medium text-orange-600">-{formatCurrency(loanData.total_fees || 0)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-green-200 bg-green-50 -mx-4 px-4 py-2 rounded">
                <span className="font-semibold text-gray-900">Amount Disbursed to Member:</span>
                <span className="font-bold text-green-600">{formatCurrency(loanData.disbursed_amount || (loanData.principal - (loanData.total_fees || 0)))}</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-blue-200">
                <span className="font-semibold text-gray-900">Total Amount to Repay:</span>
                <span className="font-bold text-blue-600">{formatCurrency(loanData.total_amount)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loan Documents */}
      <LoanDocumentViewer 
        loan={loanData} 
        onDownload={handleDownloadDocument} 
      />

      {/* Guarantors Information */}
      {loanData.loan_type?.requires_guarantors && (
        <div className="bg-white rounded-xl shadow p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Users size={20} className="mr-2 text-gray-600" />
            Guarantors
          </h3>
          
          {guarantorsLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading guarantors...</span>
            </div>
          ) : guarantorsData && guarantorsData.length > 0 ? (
            <div className="space-y-4">
              {/* Guarantor Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 kpi-grid">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="text-2xl font-bold text-blue-600">
                    {loanData.guarantors_confirmed_count || 0}
                  </div>
                  <div className="text-sm text-blue-600">Accepted Guarantors</div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="text-2xl font-bold text-green-600">
                    {formatCurrency(loanData.guarantor_amount_confirmed || 0)}
                  </div>
                  <div className="text-sm text-green-600">Total Guaranteed</div>
                </div>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <div className="text-2xl font-bold text-orange-600">
                    {(() => {
                      const requiredAmount = loanData.guarantor_amount || 0;
                      const confirmedAmount = loanData.guarantor_amount_confirmed || 0;
                      const remainingAmount = Math.max(0, requiredAmount - confirmedAmount);
                      return remainingAmount === 0 ? 'Target Met!' : formatCurrency(remainingAmount);
                    })()}
                  </div>
                  <div className="text-sm text-orange-600">
                    {(() => {
                      const requiredAmount = loanData.guarantor_amount || 0;
                      const confirmedAmount = loanData.guarantor_amount_confirmed || 0;
                      const remainingAmount = Math.max(0, requiredAmount - confirmedAmount);
                      return remainingAmount === 0 ? 'Guarantor Target Achieved' : 'Remaining Required';
                    })()}
                  </div>
                </div>
              </div>

              {/* Warning message when guarantor requirements aren't met */}
              {!guarantorRequirementsMet() && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center">
                    <AlertCircle size={20} className="text-red-600 mr-2" />
                    <div>
                      <h4 className="font-medium text-red-800">Guarantor Requirements Not Met</h4>
                      <p className="text-sm text-red-700 mt-1">
                        This loan requires KES {formatCurrency(loanData.guarantor_amount || 0)} in guarantor coverage, 
                        but only KES {formatCurrency(loanData.guarantor_amount_confirmed || 0)} has been confirmed. 
                        The loan cannot be approved until the full guarantor amount is met.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Guarantor List */}
              <div className="space-y-3">
                {guarantorsData.map((guarantor) => (
                  <div key={guarantor.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                          <User size={20} className="text-gray-600" />
                        </div>
                        <div>
                          <p className="font-medium">{guarantor.member_name || 'Unknown'}</p>
                          <p className="text-sm text-gray-500">{guarantor.member_phone || 'N/A'}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-lg">
                          {formatCurrency(guarantor.guaranteed_amount || 0)}
                        </div>
                        <div className={`text-sm px-2 py-1 rounded-full ${
                          guarantor.status === 'accepted' 
                            ? 'bg-green-100 text-green-800' 
                            : guarantor.status === 'rejected'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {guarantor.status === 'accepted' ? 'Accepted' : 
                           guarantor.status === 'rejected' ? 'Rejected' : 'Pending'}
                        </div>
                      </div>
                    </div>
                    {guarantor.confirmed_at && (
                      <div className="mt-2 text-xs text-gray-500">
                        {guarantor.status === 'accepted' ? 'Accepted' : 'Processed'} on: {new Date(guarantor.confirmed_at).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Users size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-600 mb-2">No Guarantors</h3>
              <p className="text-gray-500">
                This loan doesn't have any guarantors assigned yet.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Payment Expectations */}
      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Payment Expectations</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <Calendar size={32} className="mx-auto text-blue-600 mb-2" />
            <h4 className="font-semibold text-gray-900">{installmentLabel}</h4>
            <p className="text-2xl font-bold text-blue-600">{formatCurrency(loanData.monthly_installments || 0)}</p>
          </div>
          
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <CheckCircle size={32} className="mx-auto text-green-600 mb-2" />
            <h4 className="font-semibold text-gray-900">Payments Made</h4>
            <p className="text-2xl font-bold text-green-600">{summary?.repayment_count || 0}</p>
          </div>
          
          <div className="text-center p-4 bg-orange-50 rounded-lg">
            <Clock size={32} className="mx-auto text-orange-600 mb-2" />
            <h4 className="font-semibold text-gray-900">Next Payment Due</h4>
            <p className="text-lg font-bold text-orange-600">
              {nextPaymentDue ? new Date(nextPaymentDue).toLocaleDateString() : 'No due date'}
            </p>
          </div>
        </div>
      </div>

      {/* Repayment Timeline */}
      <div className="bg-white rounded-xl shadow">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Repayment History</h3>
            <button
              onClick={() => setShowRepaymentExportModal(true)}
              disabled={exportLoanRepayments.isLoading}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center disabled:opacity-50"
            >
              <Download size={16} className="mr-2" />
              {exportLoanRepayments.isLoading ? 'Exporting...' : 'Export Repayments'}
            </button>
          </div>
        </div>
        <LoanRepaymentTimeline 
          repayments={repayments} 
          loanDetails={loanData}
          summary={summary}
          onEditRepayment={handleEditRepayment}
          onDeleteRepayment={handleDeleteRepayment}
          isAdmin={isAdmin}
        />
      </div>

      {/* Payment Schedule */}
      <div className="bg-white rounded-xl shadow">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Calendar size={20} className="mr-2 text-blue-600" />
            Payment Schedule
          </h3>
        </div>
        <div className="p-6">
          <LoanScheduleTable loanId={parseInt(loanId)} />
        </div>
      </div>

      {/* Approval Timeline Modal */}
      {showApprovalTimeline && (
        <Modal 
          isOpen={showApprovalTimeline} 
          onClose={() => setShowApprovalTimeline(false)}
          title="Loan Approval Timeline"
        >
          <LoanStatusTimeline 
            loanId={loanId} 
            isAdmin={isAdmin} 
            onClose={() => setShowApprovalTimeline(false)}
          />
        </Modal>
      )}

      {/* Add Repayment Modal */}
      <Modal 
        isOpen={isRepaymentModalOpen} 
        onClose={() => setIsRepaymentModalOpen(false)}
        title="Add Loan Repayment"
      >
        <RepaymentForm
          loanData={loanData}
          onSubmit={handleCreateRepayment}
          onCancel={() => setIsRepaymentModalOpen(false)}
        />
      </Modal>

      {/* Edit Repayment Modal */}
      <Modal 
        isOpen={isEditModalOpen} 
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedRepayment(null);
        }}
        title="Edit Loan Repayment"
      >
        <EditRepaymentForm
          loanData={loanData}
          initialData={selectedRepayment}
          onSubmit={handleUpdateRepayment}
          onCancel={() => {
            setIsEditModalOpen(false);
            setSelectedRepayment(null);
          }}
        />
      </Modal>

      {/* M-Pesa Payment Modal */}
      <Modal 
        isOpen={isMpesaModalOpen} 
        onClose={() => setIsMpesaModalOpen(false)}
        title="Pay via M-Pesa"
      >
        <MpesaPaymentForm
          loanData={loanData}
          userProfile={userProfile}
          onSubmit={handleMpesaPayment}
          onCancel={() => setIsMpesaModalOpen(false)}
          isLoading={payLoanViaMpesa.isLoading}
        />
      </Modal>

      {/* Approval Action Modal */}
      <Modal 
        isOpen={approvalModalOpen} 
        onClose={() => setApprovalModalOpen(false)}
        title={`${approvalAction === 'approve' ? 'Approve' : 'Disapprove'} Loan`}
      >
        <div className="space-y-4">
          {loanData && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Loan Details</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Member:</span>
                  <p className="font-medium">{loanData.member?.name}</p>
                </div>
                <div>
                  <span className="text-gray-600">Amount:</span>
                  <p className="font-medium">{formatCurrency(loanData.principal)}</p>
                </div>
                <div>
                  <span className="text-gray-600">Loan Type:</span>
                  <p className="font-medium">{loanData.loan_type?.name}</p>
                </div>
                <div>
                  <span className="text-gray-600">Duration:</span>
                  <p className="font-medium">{loanData.formatted_duration || `${loanData.duration} months`}</p>
                </div>
              </div>
            </div>
          )}
          
          <p className="text-sm text-gray-700">
            Are you sure you want to <strong>{approvalAction}</strong> this loan? This action cannot be undone.
          </p>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              rows="4"
              placeholder="Add any notes for the approval/disapproval..."
              value={approvalNotes}
              onChange={(e) => setApprovalNotes(e.target.value)}
            />
          </div>
          
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
              onClick={() => setApprovalModalOpen(false)}
              disabled={isApprovalLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              className={`px-4 py-2 rounded text-white hover:opacity-90 ${
                approvalAction === 'approve' 
                  ? 'bg-green-600 hover:bg-green-700' 
                  : 'bg-red-600 hover:bg-red-700'
              }`}
              onClick={submitApproval}
              disabled={isApprovalLoading}
            >
              {isApprovalLoading ? 'Processing...' : `${approvalAction === 'approve' ? 'Approve' : 'Disapprove'} Loan`}
            </button>
          </div>
        </div>
      </Modal>

      {/* Disbursement Modal */}
      <Modal 
        isOpen={disbursementModalOpen} 
        onClose={() => setDisbursementModalOpen(false)}
        title="Disburse Loan"
      >
        <div className="space-y-4">
          {loanData && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Loan Details</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Member:</span>
                  <p className="font-medium">{loanData.member?.name}</p>
                </div>
                <div>
                  <span className="text-gray-600">Amount:</span>
                  <p className="font-medium">{formatCurrency(loanData.principal)}</p>
                </div>
                <div>
                  <span className="text-gray-600">Loan Type:</span>
                  <p className="font-medium">{loanData.loan_type?.name}</p>
                </div>
                <div>
                  <span className="text-gray-600">Duration:</span>
                  <p className="font-medium">{loanData.formatted_duration || `${loanData.duration} months`}</p>
                </div>
              </div>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Disbursement Date *
            </label>
            <input
              type="datetime-local"
              value={disbursementDate.replace(' ', 'T')}
              onChange={(e) => setDisbursementDate(e.target.value.replace('T', ' '))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={sendSmsNotification}
              onChange={(e) => setSendSmsNotification(e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm text-gray-700">Send SMS notification to borrower</span>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes (optional)
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              rows="4"
              placeholder="Additional notes for disbursement..."
              value={disbursementNotes}
              onChange={(e) => setDisbursementNotes(e.target.value)}
            />
          </div>
          
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
              onClick={() => setDisbursementModalOpen(false)}
              disabled={isDisbursementLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
              onClick={submitDisbursement}
              disabled={isDisbursementLoading}
            >
              {isDisbursementLoading ? 'Processing...' : 'Disburse Loan'}
            </button>
          </div>
        </div>
      </Modal>
      
      {/* Guarantor Request Modal */}
      <Modal 
        isOpen={guarantorModalOpen} 
        onClose={() => setGuarantorModalOpen(false)}
        title="Send Guarantor Requests"
      >
        <GuarantorRequestForm
          loanData={{
            ...loanData,
            guarantors: guarantorsData || []
          }}
          onSubmit={handleSendGuarantorRequests}
          onCancel={() => setGuarantorModalOpen(false)}
          onGuarantorSelection={handleGuarantorSelection}
          selectedGuarantors={selectedGuarantors}
          isLoading={sendGuarantorRequests.isLoading}
        />
      </Modal>

      {/* Export Format Modal */}
      <ExportFormatModal
        isOpen={showRepaymentExportModal}
        onClose={() => setShowRepaymentExportModal(false)}
        onExport={(format) => exportLoanRepayments.mutate({ loanId: parseInt(loanId), format })}
        title="Export Loan Repayments"
        subtitle="Choose the format for your repayments export:"
      />

      {/* Reapply Loan Modal */}
      <Modal
        isOpen={reapplyModalOpen}
        onClose={() => setReapplyModalOpen(false)}
        title="Reapply Loan"
      >
        <ReapplyLoanForm
          originalLoan={loanData}
          onSubmit={() => {
            setReapplyModalOpen(false);
            // Refresh the page to show the new loan
            window.location.reload();
          }}
          onCancel={() => setReapplyModalOpen(false)}
        />
      </Modal>
      
      {/* Write-Off Modal */}
      <Modal
        isOpen={writeOffModalOpen}
        onClose={() => setWriteOffModalOpen(false)}
        title="Write-Off Loan"
      >
        <LoanWriteOffForm
          loan={loanData}
          onSuccess={() => {
            setWriteOffModalOpen(false);
            queryClient.invalidateQueries(['loanRepaymentsByLoan']);
            queryClient.invalidateQueries(['memberLoans']);
          }}
          onCancel={() => setWriteOffModalOpen(false)}
        />
      </Modal>
      
      {/* Restructure Modal */}
      <Modal
        isOpen={restructureModalOpen}
        onClose={() => setRestructureModalOpen(false)}
        title="Restructure Loan"
      >
        <LoanRestructureForm
          loan={loanData}
          onSuccess={() => {
            setRestructureModalOpen(false);
            queryClient.invalidateQueries(['loanRepaymentsByLoan']);
            queryClient.invalidateQueries(['memberLoans']);
          }}
          onCancel={() => setRestructureModalOpen(false)}
        />
      </Modal>
      
      {/* Collateral Modal */}
      <Modal
        isOpen={collateralModalOpen}
        onClose={() => setCollateralModalOpen(false)}
        title="Add Collateral"
      >
        <LoanCollateralForm
          loan={loanData}
          onSuccess={() => {
            setCollateralModalOpen(false);
            queryClient.invalidateQueries(['loanRepaymentsByLoan']);
            queryClient.invalidateQueries(['memberLoans']);
          }}
          onCancel={() => setCollateralModalOpen(false)}
        />
      </Modal>
      
      {/* Refinance Modal */}
      <Modal
        isOpen={refinanceModalOpen}
        onClose={() => setRefinanceModalOpen(false)}
        title="Refinance Loans"
      >
        <LoanRefinanceForm
          member={loanData.member}
          onSuccess={() => {
            setRefinanceModalOpen(false);
            queryClient.invalidateQueries(['loanRepaymentsByLoan']);
            queryClient.invalidateQueries(['memberLoans']);
          }}
          onCancel={() => setRefinanceModalOpen(false)}
        />
      </Modal>
    </div>
    
  );
}

function EditRepaymentForm({ loanData, initialData, onSubmit, onCancel }) {
  const [amount, setAmount] = useState(initialData?.amount || '');
  const [paymentDate, setPaymentDate] = useState(initialData?.payment_date || '');
  const [paymentMethod, setPaymentMethod] = useState(initialData?.payment_method || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [transactionReference, setTransactionReference] = useState(initialData?.transaction_reference || '');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      amount: parseFloat(amount),
      payment_date: paymentDate,
      payment_method: paymentMethod,
      description: description,
      transaction_reference: transactionReference,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
        <input
          type="number"
          step="0.01"
          min="0"
          max={loanData.loan_balance}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          required
        />
        <p className="text-xs text-gray-500 mt-1">
          Outstanding balance: {formatCurrency(loanData.loan_balance)}
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date *</label>
        <input
          type="date"
          value={paymentDate}
          onChange={(e) => setPaymentDate(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method *</label>
        <select
          value={paymentMethod}
          onChange={(e) => setPaymentMethod(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          required
        >
          <option value="">Select payment method</option>
          {PAYMENT_METHOD_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows="3"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          placeholder="Additional notes about this payment..."
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Reference</label>
        <input
          type="text"
          value={transactionReference}
          onChange={(e) => setTransactionReference(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          placeholder="e.g., MPESA transaction ID"
        />
      </div>

      <div className="flex justify-end space-x-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
        >
          Update Payment
        </button>
      </div>
    </form>
  );
}

function RepaymentForm({ loanData, onSubmit, onCancel }) {
  const [amount, setAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [description, setDescription] = useState('');
  const [transactionReference, setTransactionReference] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      loan_id: loanData.id,
      amount: parseFloat(amount),
      payment_date: paymentDate,
      payment_method: paymentMethod,
      description: description,
      transaction_reference: transactionReference,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
        <input
          type="number"
          step="0.01"
          min="0"
          max={loanData.loan_balance}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          required
        />
        <p className="text-xs text-gray-500 mt-1">
          Outstanding balance: {formatCurrency(loanData.loan_balance)}
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date *</label>
        <input
          type="date"
          value={paymentDate}
          onChange={(e) => setPaymentDate(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method *</label>
        <select
          value={paymentMethod}
          onChange={(e) => setPaymentMethod(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          required
        >
          <option value="">Select payment method</option>
          {PAYMENT_METHOD_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows="3"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          placeholder="Additional notes about this payment..."
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Reference</label>
        <input
          type="text"
          value={transactionReference}
          onChange={(e) => setTransactionReference(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          placeholder="e.g., MPESA transaction ID"
        />
      </div>

      <div className="flex justify-end space-x-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
        >
          Add Payment
        </button>
      </div>
    </form>
  );
}

function getStatusColor(status) {
  const colors = {
    'PENDING': 'bg-yellow-100 text-yellow-800',
    'APPROVED': 'bg-green-100 text-green-800',
    'DISAPPROVED': 'bg-red-100 text-red-800',
    'ACTIVE': 'bg-blue-100 text-blue-800',
    'COMPLETED': 'bg-gray-100 text-gray-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
}

function calculateNextPaymentDue(loanData, repayments) {
  if (!loanData || !repayments.length) return null;
  
  // Calculate total amount paid
  const totalPaid = repayments.reduce((sum, payment) => sum + parseFloat(payment.amount), 0);
  
  // Calculate how many full installments have been paid
  const fullInstallmentsPaid = Math.floor(totalPaid / loanData.monthly_installments);
  
  // Get effective duration and duration unit
  const effectiveDuration = loanData.effective_duration || loanData.duration;
  const durationUnit = loanData.loan_type_duration_unit || 'months';
  
  // If we've paid more installments than the loan duration, loan is complete
  if (fullInstallmentsPaid >= effectiveDuration) {
    return null;
  }
  
  // Calculate the next due date based on full installments paid and duration unit
  const startDate = new Date(loanData.start_date);
  const nextDueDate = new Date(startDate);
  
  switch (durationUnit) {
    case 'days':
      nextDueDate.setDate(nextDueDate.getDate() + fullInstallmentsPaid);
      break;
    case 'weeks':
      nextDueDate.setDate(nextDueDate.getDate() + (fullInstallmentsPaid * 7));
      break;
    case 'months':
    default:
      nextDueDate.setMonth(nextDueDate.getMonth() + fullInstallmentsPaid);
      break;
  }
  
  return nextDueDate;
}

function calculateFullInstallmentsPaid(loanData, repayments) {
  if (!loanData || !repayments.length) return 0;
  
  const totalPaid = repayments.reduce((sum, payment) => sum + parseFloat(payment.amount), 0);
  return Math.floor(totalPaid / loanData.monthly_installments);
}

function calculateRemainingInstallments(loanData, repayments) {
  const fullInstallmentsPaid = calculateFullInstallmentsPaid(loanData, repayments);
  const effectiveDuration = loanData.effective_duration || loanData.duration;
  return Math.max(0, effectiveDuration - fullInstallmentsPaid);
}

function isCurrentMonthFullyPaid(loanData, repayments) {
  if (!loanData || !repayments.length) return false;
  
  const totalPaid = repayments.reduce((sum, payment) => sum + parseFloat(payment.amount), 0);
  const fullInstallmentsPaid = Math.floor(totalPaid / loanData.monthly_installments);
  const expectedPaid = fullInstallmentsPaid * loanData.monthly_installments;
  
  return totalPaid >= expectedPaid;
}

function getCurrentInstallmentRemaining(loanData, repayments) {
  if (!loanData || !repayments.length) return loanData.monthly_installments;
  
  const totalPaid = repayments.reduce((sum, payment) => sum + parseFloat(payment.amount), 0);
  const fullInstallmentsPaid = Math.floor(totalPaid / loanData.monthly_installments);
  const expectedPaid = fullInstallmentsPaid * loanData.monthly_installments;
  
  // If we've paid more than expected for this installment, return 0
  if (totalPaid >= expectedPaid + loanData.monthly_installments) {
    return 0;
  }
  
  // Return the remaining amount for the current installment
  return Math.max(0, loanData.monthly_installments - (totalPaid - expectedPaid));
}

function getCurrentInstallmentPaid(loanData, repayments) {
  if (!loanData || !repayments.length) return 0;
  
  const totalPaid = repayments.reduce((sum, payment) => sum + parseFloat(payment.amount), 0);
  const fullInstallmentsPaid = Math.floor(totalPaid / loanData.monthly_installments);
  const expectedPaid = fullInstallmentsPaid * loanData.monthly_installments;
  
  // Return the amount paid for the current installment
  return totalPaid - expectedPaid;
}

function MpesaPaymentForm({ loanData, userProfile, onSubmit, onCancel, isLoading }) {
  const [phoneNumber, setPhoneNumber] = useState(userProfile?.data?.phone_number || '');
  const [amount, setAmount] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      loan_id: loanData.id,
      phone_number: phoneNumber,
      amount: parseFloat(amount),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
        <input
          type="tel"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          placeholder="254700000000"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
        <input
          type="number"
          step="0.01"
          min="0"
          max={loanData.loan_balance}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          required
        />
        <p className="text-xs text-gray-500 mt-1">
          Outstanding balance: {formatCurrency(loanData.loan_balance)}
        </p>
      </div>

      <div className="flex justify-end space-x-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
          disabled={isLoading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700"
          disabled={isLoading}
        >
          {isLoading ? 'Processing...' : 'Pay via M-Pesa'}
        </button>
      </div>
    </form>
  );
} 